import { IconProp, SizeProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { Children, MouseEventHandler, useEffect } from "react";
import {
  faChevronRight,
  faEllipsis,
  faChevronLeft,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/pro-regular-svg-icons";
import useTooltip from "../../hooks/useTooltip";
import { useBottomSheet } from "@/components/shared/hooks/mobile/useBottomSheet.tsx";
import BottomSheet from "@/components/shared/mobile/BottomSheet.tsx";

function closeAllDropdowns() {
  const allDropdowns = document.querySelectorAll(".tixxt__dropdown");
  allDropdowns.forEach((dropdown) => {
    dropdown.classList.remove("show");
  });
}

type DropDownArgs = {
  children: React.ReactNode;
  alignment?: "start" | "end";
  className?: string;
};

type DropdownContextValue = {
  onItemSelect?: () => void;
};

export const DropdownContext = React.createContext<DropdownContextValue | null>(
  null,
);

function DropDown({ children, className, alignment = "end" }: DropDownArgs) {
  // make sure to close dropdowns on body click
  useEffect(() => {
    document.body.addEventListener("click", closeAllDropdowns);

    return () => {
      document.body.removeEventListener("click", closeAllDropdowns);
    };
  }, []);

  return (
    <ul
      role="menu"
      className={classNames(
        "tixxt__dropdown",
        !window.isApp &&
          `dropdown-menu dropdown-menu-${alignment} empty:hidden border-box`,
        className,
      )}
    >
      {children}
    </ul>
  );
}

export function DropDownSubMenu(props: Omit<DropDownNestedMenuArgs, "level">) {
  return <DropDownNestedMenu {...props} level={1} />;
}

export function DropDownSubSubMenu(
  props: Omit<DropDownNestedMenuArgs, "level">,
) {
  return <DropDownNestedMenu {...props} level={2} />;
}

type DropDownItemArgs = {
  children?: React.ReactNode;
  url?: string;
  icon?: IconProp;
  text?: string;
  additional?: React.ReactNode;
  linkStyle?: string;
  iconStyle?: React.CSSProperties;
  method?: string;
  confirm?: string;
  className?: string;
  iconClassName?: string;
  onSelect?: MouseEventHandler<HTMLAnchorElement>;
  title?: string;
  dividerTop?: boolean;
  dividerBottom?: boolean;
  target?: string | null;
  data?: { [key: string]: string | boolean };
  propagateClick?: boolean;
};

export function DropDownItem({
  children,
  url,
  icon,
  text,
  additional,
  linkStyle,
  iconStyle,
  method,
  confirm,
  className,
  iconClassName,
  onSelect,
  title,
  dividerTop,
  dividerBottom,
  target,
  data,
  propagateClick = false,
}: DropDownItemArgs) {
  const dropdownContext = React.useContext(DropdownContext);
  const ItemWrapperComponent = ItemWrapper;
  return (
    <>
      {dividerTop && <li className="divider" />}
      <ItemWrapperComponent className={className} title={title}>
        {children ? (
          children
        ) : (
          <a
            href={url || "#"}
            className={classNames("dropdown-item", linkStyle)}
            role="menuitem"
            data-method={method}
            data-confirm={confirm}
            onClick={(e) => {
              if (onSelect) {
                if (!propagateClick) e.stopPropagation();
                e.preventDefault();
                onSelect(e);
                dropdownContext?.onItemSelect?.();
              }
            }}
            target={target || undefined}
            {...data}
          >
            <span className="flex items-center gap-1">
              {icon && (
                <FontAwesomeIcon
                  icon={icon}
                  className={classNames("fa-fw", iconClassName)}
                  style={iconStyle}
                />
              )}
              {text && <span className="flex-1">{text}</span>}
              {additional}
            </span>
          </a>
        )}
      </ItemWrapperComponent>
      {dividerBottom && <li className="divider" />}
    </>
  );
}

type ItemWrapperProps = {
  className?: string;
  title?: string;
  children: React.ReactNode;
};

function ItemWrapper({ className, title, children }: ItemWrapperProps) {
  const tooltipRef = useTooltip<HTMLLIElement>(title);
  return (
    <li ref={tooltipRef} className={className} title={title}>
      {children}
    </li>
  );
}
type DropDownNestedMenuArgs = {
  text: string;
  children: React.ReactNode;
  className?: string;
  icon?: IconProp;
  iconClassName?: string;
  side?: "left" | "right" | "bottom";
  dividerTop?: boolean;
  dividerBottom?: boolean;
  level: number;
};

function DropDownNestedMenu({
  text,
  children,
  className,
  icon,
  iconClassName,
  side = "right",
  dividerTop,
  dividerBottom,
  level,
}: DropDownNestedMenuArgs) {
  const submenuRef = React.useRef<HTMLUListElement>(null);
  const [showSubmenu, setShowSubmenu] = React.useState(
    !!submenuRef?.current?.classList.contains("show"),
  );
  const levelClass = `level-${level}`;

  useEffect(() => {
    if (!submenuRef.current) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          setShowSubmenu(!!submenuRef?.current?.classList.contains("show"));
        }
      });
    });

    observer.observe(submenuRef.current, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      {dividerTop && <li className="divider" />}
      <li
        className={classNames(`relative group/${levelClass}`, className)}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();

          if (showSubmenu) {
            submenuRef?.current?.classList.add("hidden");
            submenuRef.current?.classList.remove("show");
          } else {
            submenuRef?.current?.classList.add("show");
            submenuRef.current?.classList.remove("hidden");
          }
        }}
      >
        <div
          className="dropdown-item flex gap-2 items-center w-full text-left cursor-pointer"
          role="menuitem"
          aria-haspopup="menu"
          aria-expanded={showSubmenu}
        >
          <span className="flex items-center space-x-1">
            {side === "left" && !window.isApp && (
              <FontAwesomeIcon icon={faChevronLeft} className="ml-auto" />
            )}
            {icon && (
              <FontAwesomeIcon
                icon={icon}
                className={classNames("fa-fw", iconClassName)}
              />
            )}
            <span className="flex-1">{text}</span>
            {level === 2 &&
              (side === "right" || side === "bottom" || window.isApp) && (
                <FontAwesomeIcon
                  icon={
                    window.isApp
                      ? showSubmenu
                        ? faChevronUp
                        : faChevronDown
                      : faChevronRight
                  }
                  className="ml-auto"
                />
              )}
          </span>
          {level === 1 &&
            (side === "right" || side === "bottom" || window.isApp) && (
              <FontAwesomeIcon
                icon={
                  window.isApp
                    ? showSubmenu
                      ? faChevronUp
                      : faChevronDown
                    : faChevronRight
                }
                className="ml-auto"
              />
            )}
        </div>
        <ul
          ref={submenuRef}
          role="menu"
          className={classNames("tixxt__dropdown hidden", {
            "dropdown-menu absolute top-0 mt-0": !window.isApp,
            "left-full ml-1": side === "right" && !window.isApp,
            "right-full mr-1": side === "left" && !window.isApp,
            "left-4 top-0": side === "bottom" && !window.isApp,
            "group-hover/level-1:block": level === 1,
            "group-hover/level-2:block": level === 2,
            "pl-2.5": window.isApp && level === 1,
            "pl-5": window.isApp && level === 2,
          })}
        >
          {children}
        </ul>
      </li>
      {dividerBottom && <li className="divider" />}
    </>
  );
}

type DropdownToggleArgs = {
  title?: string;
  active?: boolean;
  disabled?: boolean;
  small?: boolean;
  icon?: IconProp;
  className?: string;
  iconClassName?: string;
  iconSize?: SizeProp;
  buttonType?: "light" | "primary" | "danger" | "normal" | "link";
  children?: React.ReactNode;
  showIcon?: boolean;
  ariaLabel?: string;
  dataTestid?: string;
  onToggle?: () => void;
};

export function DropDownToggle({
  active = false,
  disabled = false,
  small = false,
  showIcon = true,
  title,
  icon,
  className,
  buttonType = "light",
  iconClassName,
  children,
  iconSize,
  ariaLabel,
  dataTestid,
  onToggle,
}: DropdownToggleArgs) {
  function toggleDropdown(e) {
    e.stopPropagation();

    closeAllDropdowns();

    if (window.isApp && onToggle) {
      onToggle();
    } else {
      const currentDropdown = e.currentTarget.nextElementSibling;
      if (
        currentDropdown &&
        currentDropdown.classList.contains("tixxt__dropdown")
      ) {
        currentDropdown.classList.add("show");
      }
    }
  }

  return (
    <button
      // Without an explicit type a button defaults to type="submit" and
      // silently submits any surrounding form when the dropdown is opened
      type="button"
      className={classNames("btn dropdown-toggle space-x-1", className, {
        active,
        "btn-light": buttonType === "light",
        "btn-primary": buttonType === "primary",
        "btn-danger": buttonType === "danger",
        "btn-link": buttonType === "link",
        "btn-normal": buttonType === "normal",
        "btn-sm": small,
        "btn-mobile": window.isApp,
      })}
      data-bs-toggle="dropdown"
      data-testid={dataTestid}
      title={title}
      disabled={disabled}
      onClick={toggleDropdown}
      role="button"
      aria-label={ariaLabel}
      aria-haspopup="menu"
    >
      {showIcon && (
        <FontAwesomeIcon
          className={classNames(iconClassName, {
            "mr-1.5": Children.count(children) > 0,
          })}
          icon={icon || faEllipsis}
          size={iconSize}
        />
      )}
      {children}
    </button>
  );
}

/**
 * Wrapper for DropDown that handles mobile BottomSheet behavior.
 *
 * @param showToggleButton - When false, the DropDownToggle is not rendered.
 *   This only works because on non-mobile (!window.isApp), the wrapper returns
 *   children directly without modification. On mobile, hiding the toggle is useful
 *   when the dropdown is triggered by other means (e.g., long-press).
 * @param showDropDown - External control for opening the sheet (e.g., from long-press)
 * @param onClose - Callback when sheet closes (e.g., backdrop tap, swipe)
 */
export function DropDownWrapper({
  showDropDown,
  onClose,
  showToggleButton = true,
  children,
}: {
  children: React.ReactNode;
  showDropDown?: boolean;
  showToggleButton?: boolean;
  onClose?: () => void;
}) {
  const { showSheet, isAnimating, toggleSheet, closeSheet, openSheet } =
    useBottomSheet();

  useEffect(() => {
    if (showDropDown) openSheet();
  }, [showDropDown, openSheet]);

  useEffect(() => {
    if (!showSheet) onClose?.();
  }, [showSheet, onClose]);

  // On non-mobile, render children directly - DropDown handles its own visibility
  if (!window.isApp) return <>{children}</>;

  return (
    <>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement<DropdownToggleArgs | DropDownArgs>(child))
          return child;

        if (child.type === DropDownToggle) {
          return (
            showToggleButton &&
            React.cloneElement(child, {
              onToggle: toggleSheet,
            })
          );
        }

        if (child.type === DropDown) {
          return (
            <BottomSheet
              showSheet={showSheet}
              isAnimating={isAnimating}
              onClose={closeSheet}
            >
              <DropdownContext.Provider
                value={{
                  onItemSelect: closeSheet,
                }}
              >
                {React.cloneElement(child)}
              </DropdownContext.Provider>
            </BottomSheet>
          );
        }

        return child;
      })}
    </>
  );
}

DropDown.Item = DropDownItem;
DropDown.SubMenu = DropDownSubMenu;
DropDown.SubSubMenu = DropDownSubSubMenu;
DropDown.Toggle = DropDownToggle;
DropDown.Wrapper = DropDownWrapper;

export default DropDown;
